"use client"

import React, { ReactNode, useEffect, useRef } from "react"
import AppIcon from "@/app/components/atomic/AppIcon"

interface Props {
    label: string
    icon?: ReactNode
    value: ReactNode
    placeholder: string
    open: boolean
    onToggle: () => void
    panelClassName?: string
    onDone?: () => void
    children: ReactNode
}

// The parent owns the `relative` wrapper and the click-outside ref, so the panel can anchor below the card
export default function TripFieldCard({
    label,
    icon,
    value,
    placeholder,
    open,
    onToggle,
    panelClassName = "",
    onDone,
    children,
}: Props) {
    const hasValue = value !== null && value !== undefined && value !== ""
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (open) {
            panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
        }
    }, [open])

    return (
        <>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex w-full flex-col gap-1 rounded-[14px] border border-rm-gray-light bg-white px-3.5 py-3 text-left transition-colors hover:border-rm-gray focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue"
            >
                <span className="text-xs font-bold uppercase tracking-wide text-rm-gray-dark">{label}</span>
                <span className="flex w-full items-center justify-between gap-2 text-[15px]">
                    <span className={`flex min-w-0 items-center gap-2 ${hasValue ? "text-pale" : "text-[#9AA0AC]"}`}>
                        {icon}
                        <span className="truncate">{hasValue ? value : placeholder}</span>
                    </span>
                    <AppIcon
                        iconClassName="fa-chevron-down"
                        size="xs"
                        className={`shrink-0 text-[#4a5565] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    />
                </span>
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className={`absolute left-0 top-[calc(100%+8px)] z-50 rounded-[14px] border border-rm-gray-light bg-white shadow-lg ${panelClassName}`}
                >
                    {children}
                    {onDone && (
                        <div className="mt-2 border-t border-gray-100 p-2">
                            <button
                                type="button"
                                onClick={onDone}
                                className="h-11 w-full rounded-xl bg-rm-blue text-[15px] font-bold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue focus-visible:ring-offset-1"
                            >
                                Valider
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}
